import {
  PARTNER_REFERRAL_TYPED_CODE_COOKIE,
  PARTNER_REFERRAL_TYPED_CODE_WINDOW_MINUTES,
} from "./constants";
import type { PartnerReferralCookieValue } from "./types";

/**
 * Parse a raw cookie value back into a referral. Returns null when the value
 * is missing or malformed.
 */
export function parsePartnerReferral(
  raw: string | undefined | null,
): PartnerReferralCookieValue | null {
  if (!raw) return null;

  try {
    const data = JSON.parse(
      decodeURIComponent(raw),
    ) as Partial<PartnerReferralCookieValue>;

    if (
      !data ||
      typeof data.partnerId !== "string" ||
      typeof data.slug !== "string" ||
      (data.via !== "link" && data.via !== "code")
    ) {
      return null;
    }

    return {
      partnerId: data.partnerId,
      slug: data.slug,
      via: data.via,
      landedAt:
        typeof data.landedAt === "string"
          ? data.landedAt
          : new Date().toISOString(),
    };
  } catch {
    return null;
  }
}

/**
 * Persist a typed alias code so it survives a social sign-in redirect, where no
 * post-auth client hook runs. The server `user.create` hook reads it. No-op on
 * the server or for an empty code.
 */
export function writePartnerTypedCodeCookie(code: string): void {
  if (typeof document === "undefined") return;

  const trimmed = code.trim();
  if (!trimmed) return;

  const maxAge = PARTNER_REFERRAL_TYPED_CODE_WINDOW_MINUTES * 60;
  document.cookie = `${PARTNER_REFERRAL_TYPED_CODE_COOKIE}=${encodeURIComponent(
    trimmed,
  )}; path=/; max-age=${maxAge}; samesite=lax`;
}

/**
 * Decode a typed-code cookie value written by `writePartnerTypedCodeCookie`
 * (URL-encoded). Falls back to the raw value if decoding fails.
 */
export function decodePartnerTypedCodeCookie(
  raw: string | undefined | null,
): string {
  if (!raw) return "";
  try {
    return decodeURIComponent(raw);
  } catch {
    return raw;
  }
}

/**
 * Expire the typed-code handoff cookie once the claim has been consumed, so an
 * abandoned sign-in can't attribute a later, unrelated signup in the same
 * browser. No-op on the server.
 */
export function readPartnerTypedCodeCookie(): string {
  if (typeof document === "undefined") return "";
  const prefix = `${PARTNER_REFERRAL_TYPED_CODE_COOKIE}=`;
  const raw = document.cookie
    .split(";")
    .map((part) => part.trim())
    .find((part) => part.startsWith(prefix))
    ?.slice(prefix.length);
  return decodePartnerTypedCodeCookie(raw);
}

export function clearPartnerTypedCodeCookie(): void {
  if (typeof document === "undefined") return;
  document.cookie = `${PARTNER_REFERRAL_TYPED_CODE_COOKIE}=; path=/; max-age=0; samesite=lax`;
}
